/**
 * warmInspectionsForOffline — on login (while online) pre-cache everything
 * needed to OPEN any of the user's inspections offline, not just ones visited
 * this session. The sync pull already mirrors the normalized rows; this fills
 * the gap by caching the enriched read-only DTOs (inspection detail, questions
 * list, summary digest) the screen needs to render, plus warming each answer
 * bundle into the mirror. Best-effort and throttled.
 */
import { api, tokens, ApiError } from "@/lib/api-client";
import { apiUrl } from "@/lib/env";
import type { Inspection, Checklist, ChecklistQuestion, QuestionAnswer, QuestionAspectResponse, InspectionHumanRankResponse, PhotoComparison, Attachment, FindingAttachment, NegativeObservation, Rectification, Vessel, Notification, Company, DeficiencyFindingWithCodes, DeficiencyFindingListItem, DeficiencyCode, ActionCode, ObservationRootCause } from "@/types/api";
import type { Repo } from "./repo";
import type { PullDelta } from "./types";
import { ck } from "./cache-keys";

/** An empty delta — spread and override the fields you actually write. */
const EMPTY_DELTA: PullDelta = {
  serverTime: "", inspections: [], answers: [], aspects: [], humanRankResponses: [],
  photoComparisons: [], negativeObservations: [], rectifications: [], ranks: [],
};

/** Per-file cap on offline-cached attachment bytes (skip very large files). */
const ASSET_MAX_BYTES = 20_000_000;
const CACHED_CATEGORIES = new Set(["image", "camera", "audio", "document"]);

/** kv key holding { inspectionId → updatedAt } for the inspections already
 *  warmed. Lets a cold start SKIP re-caching everything that hasn't changed —
 *  the expensive per-inspection loop then only touches new/changed inspections. */
const WARMED_VERSIONS_KEY = "warmup:inspection-versions";

/** Token-bucket rate gate for warm-up. Rate limiting is now PER-USER
 *  (~1000 req/min ≈ 16/s), and the consolidated bundle endpoint collapses the
 *  per-inspection JSON fan-out, so the few remaining gated calls (attachment
 *  bytes, deficiency-log findings) can run much faster than the old 4/s without
 *  starving the user's own requests. */
const WARM_RATE_PER_SEC = 12;
const WARM_BURST = 24;
let warmTokens = WARM_BURST;
let warmLast = Date.now();
function gated<T>(fn: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const take = () => {
      const now = Date.now();
      warmTokens = Math.min(WARM_BURST, warmTokens + ((now - warmLast) / 1000) * WARM_RATE_PER_SEC);
      warmLast = now;
      if (warmTokens >= 1) { warmTokens -= 1; fn().then(resolve, reject); }
      else setTimeout(take, 250);
    };
    take();
  });
}

/** Download one attachment's bytes (same-origin /raw) and cache them for
 *  offline display. Best-effort; size/category-capped; rate-gated. */
async function cacheAsset(repo: Repo, att: Attachment): Promise<void> {
  if (!CACHED_CATEGORIES.has(att.category) || (att.sizeBytes && att.sizeBytes > ASSET_MAX_BYTES)) return;
  // Already cached (a prior warm-up) → skip the re-download. Keeps re-warms and
  // the every-warm attachment refresh below cheap: only NEW bytes hit the wire.
  try { if (await repo.getAsset(att.id)) return; } catch { /* fall through and fetch */ }
  try {
    const res = await gated(() => fetch(apiUrl(`/api/attachments/${att.id}/raw`), {
      headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {},
      credentials: "include",
    }));
    if (res.ok) await repo.putAsset(att.id, await res.blob());
  } catch { /* best-effort */ }
}

export interface WarmCtx { userId: number | null; role: string | null; rank: string | null; companyId: number | null }
type QuestionAnswerSummaryRow = { questionId: number; status: string; hasNom: boolean; severities?: unknown };

/** A network/transport failure (offline, timeout, 5xx, 429) — distinct from a
 *  benign 4xx (e.g. an endpoint that doesn't apply to this inspection). Used to
 *  decide whether warm-up actually COMPLETED or was cut short by connectivity
 *  loss and must be retried after reconnect rather than treated as done. */
export function isWarmNetworkError(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 0 || e.status === 408 || e.status === 429 || e.status >= 500;
  if (e instanceof TypeError) return true; // fetch() rejected — no connection
  const m = e instanceof Error ? e.message.toLowerCase() : "";
  return m.includes("failed to fetch") || m.includes("load failed") || m.includes("networkerror");
}

export interface WarmResult {
  /** True only if warm-up finished without a connectivity failure. When false,
   *  the caller must re-warm after reconnect (not just delta-sync). */
  complete: boolean;
  /** Inspection ids fully cached this run — fed back as skipIds on a retry so a
   *  resumed warm-up only fills the gaps. */
  warmedIds: number[];
}
export interface WarmOpts { skipIds?: Set<number> }

async function mapLimit<T>(items: T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  let i = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (i < items.length) {
      const item = items[i++]!;
      await fn(item);
    }
  });
  await Promise.all(workers);
}

/** Warm the offline cache for every inspection the user can see. The list is
 *  fetched from the REST API (the same endpoint the inspection list page uses)
 *  rather than the local mirror, so warming works even if the custom
 *  /api/sync/pull populated nothing. The inspection rows are also written into
 *  the mirror so repo.getInspection() resolves offline. Safe to call repeatedly. */
export async function warmInspectionsForOffline(repo: Repo, ctx: WarmCtx, opts: WarmOpts = {}): Promise<WarmResult> {
  // Tracks whether anything failed due to connectivity (→ warm-up incomplete,
  // retry after reconnect) and which inspections got fully cached (→ skip on a
  // resumed run). Best-effort writes still never throw.
  let networkLoss = false;
  const warmedIds: number[] = [];
  const skipIds = opts.skipIds ?? new Set<number>();
  let inspections: Inspection[] = [];
  try {
    // PIQs are EXCLUDED from the default list (their own page), so fetch them
    // explicitly. Also union with the MIRROR: the sync pull (which ran just
    // before this) can surface in-scope inspections — notably company PIQs for
    // shore_staff — that the REST list filters out. Warming the union means
    // every inspection we know about gets its detail/questions/summary cached.
    const [list, piqs] = await Promise.all([
      api.paged<Inspection>("/api/inspections", { limit: 500 }),
      api.paged<Inspection>("/api/inspections", { type: "piq", limit: 500 }),
    ]);
    const byId = new Map<number, Inspection>();
    for (const i of await repo.listInspections()) byId.set(i.id, i);
    for (const i of [...(list.data ?? []), ...(piqs.data ?? [])]) byId.set(i.id, i);
    inspections = [...byId.values()];
  } catch (e) {
    if (isWarmNetworkError(e)) networkLoss = true;
    inspections = await repo.listInspections(); // offline / error → use what's mirrored
  }
  // Seed the inspection rows into the mirror up-front (independent of the pull).
  // Best-effort: a write hiccup here must NOT abort the per-inspection caching
  // below (otherwise questions/answers never get cached → "no questions" offline).
  if (inspections.length) {
    try { await repo.applyDelta({ ...EMPTY_DELTA, inspections }); } catch { /* best-effort */ }
  }

  // ── Question LIBRARIES first (the offline-critical piece) ───────────────────
  // Opening an inspection's questions offline ultimately falls back to the
  // checklist library at `checklist-questions:<cid>` (localTempQuestions in
  // InspectionQuestions.tsx, via readThrough). That library used to be assembled
  // ONLY from the per-inspection question loop below and written AFTER it
  // finished — so going offline before that slow, rate-gated loop completed left
  // every inspection showing "No questions available" even though the list (from
  // the fast sync pull) was visible. Seed each DISTINCT checklist's full library
  // up-front instead: a few cheap fetches (SIRE ≈ 450 questions, one page) that
  // make ALL of that checklist's inspections openable offline immediately,
  // decoupled from the per-inspection loop. Same endpoint + key the New-
  // inspection wizard uses, so it's reachable by vessel (Master) / shore_staff.
  // Best-effort; the per-inspection union below still backfills anything this
  // misses (notably the unpublished global PIQ checklist, if forbidden here).
  const distinctChecklistIds = [
    ...new Set(inspections.map((i) => i.checklistId).filter((c): c is number => c != null)),
  ];
  await mapLimit(distinctChecklistIds, 3, async (cid) => {
    try {
      if ((await repo.kvGet(`checklist-questions:${cid}`)) !== undefined) return;
      // No vesselType/isPiq narrowing → cache the FULL library so it's a superset
      // for every inspection on this checklist (each inspection re-narrows by its
      // own metadata.selectedQuestionIds in localTempQuestions).
      const lib = await gated(() => api.paged<ChecklistQuestion>(`/api/checklists/${cid}/questions`, { limit: 1000 }));
      await repo.kvPut(`checklist-questions:${cid}`, lib);
    } catch { /* best-effort — per-inspection union below backfills */ }
  });

  // ── Incremental scope ───────────────────────────────────────────────────────
  // Two-level skip so a warm-up only touches what it must:
  //   • `versions` ({ inspectionId → updatedAt }) PERSISTS across app restarts —
  //     a cold start re-warms only NEW or CHANGED inspections, not everything.
  //   • `skipIds` is the in-session set the caller feeds back on a failure-
  //     recovery retry so a resumed warm-up only fills the gaps it missed.
  // An inspection is warmed unless it's already been warmed this session AND is
  // unchanged since it was last cached. First-ever warm (empty map) warms all.
  const versions = (await repo.kvGet<Record<string, string>>(WARMED_VERSIONS_KEY)) ?? {};
  const toWarm = inspections.filter((i) => {
    if (skipIds.has(i.id)) return false;
    const sig = String(i.updatedAt ?? "");
    return !(sig && versions[String(i.id)] === sig);
  });

  // ── ONE consolidated warm-up call ──────────────────────────────────────────
  // detail/summary/answers/questions + response/inspection attachments per
  // inspection, PLUS aux (per-NOM root-cause & evidence bundle + observation
  // stats) — all folded into a single request. Falls back to the per-endpoint
  // fetches below only against an older backend, or on the incremental
  // (ids-subset) path where aux isn't returned.
  type AnswersBundle = { envelopes: QuestionAnswer[]; aspects: QuestionAspectResponse[]; photoComparisons: PhotoComparison[]; humanRankResponses: InspectionHumanRankResponse[] };
  type QuestionsPayload = { data?: ChecklistQuestion[] } | undefined;
  // Deficiency-log sub-payload (USCG / PSC / Flag) folded into each entry so the
  // findings / codes / per-finding data warm in the same bundle call.
  type WarmDeficiencyLog = {
    regime: string;
    findings: DeficiencyFindingWithCodes[];
    findingDetails: Record<string, DeficiencyFindingListItem>;
    findingRootCauses: Record<string, ObservationRootCause[]>;
    findingAttachments: Record<string, Attachment[]>;
    pscDeficiencyCodes: DeficiencyCode[];
    pscActionCodes: ActionCode[];
  };
  type WarmEntry = {
    detail: unknown; summary: QuestionAnswerSummaryRow[]; answers: AnswersBundle;
    questions?: QuestionsPayload; responseAttachments?: Attachment[]; inspectionAttachments?: Attachment[];
    deficiencyLog?: WarmDeficiencyLog;
  };
  type WarmAux = { negativeObservations?: { byNom?: Record<string, { rootCauses?: unknown; attachments?: Attachment[] }>; stats?: unknown } };
  // `toWarm` (computed above from versions + skipIds) is the incremental subset —
  // when everything is unchanged/already-warmed we skip the bundle fetch + loop.
  const warmBundle = new Map<number, WarmEntry>();
  let warmAux: WarmAux = {};
  if (toWarm.length > 0) {
    // Warm in id-batches of ≤25 so the server hydrates a BOUNDED set per request.
    // The endpoint buffers the whole response in memory (detail+answers+questions
    // +attachments per inspection), so an unbounded default bundle (up to 300)
    // can OOM a small instance under concurrency — the same failure we fixed on
    // /api/sync/pull. Sending explicit ids also warms EXACTLY what this client
    // needs (the union list), rather than the server's default 300-most-recent.
    // aux (NOM root-cause/stats) isn't returned on the ids path; the cross-
    // inspection block below fetches it via its standalone fallback.
    const WARMUP_CHUNK = 25;
    const ids = toWarm.map((i) => i.id);
    for (let i = 0; i < ids.length; i += WARMUP_CHUNK) {
      const batch = ids.slice(i, i + WARMUP_CHUNK);
      try {
        const url = `/api/inspections/warmup-bundle?ids=${batch.join(",")}`;
        const resp = await gated(() => api.get<{ data?: Array<WarmEntry & { id: number }>; aux?: WarmAux }>(url));
        for (const r of resp.data ?? []) warmBundle.set(r.id, r);
        if (resp.aux && Object.keys(resp.aux).length) warmAux = resp.aux;
      } catch (e) {
        // Connectivity loss → warm-up incomplete; stop (later batches would fail
        // too). Any other per-batch error is best-effort — skip and continue.
        if (isWarmNetworkError(e)) { networkLoss = true; break; }
      }
    }
  }

  // Cross-inspection data — cached so the observations list, rectifications
  // list and dashboards work offline WITHOUT visiting each page. The list/stats
  // pages read these from the mirror (entities) or fixed kv keys (aggregates).
  await Promise.all([
    (async () => {
      let obsRows: NegativeObservation[] = [];
      try {
        const obs = await api.paged<NegativeObservation>("/api/negative-observations", { limit: 1000 });
        obsRows = obs.data ?? [];
        if (obsRows.length) await repo.applyDelta({ ...EMPTY_DELTA, negativeObservations: obsRows });
      } catch { /* best-effort */ }
      // Stats: prefer the value folded into the warm-up bundle's aux; only hit
      // the standalone endpoint if aux is absent (older backend / incremental run).
      try { await repo.kvPut("nobs:stats", warmAux.negativeObservations?.stats ?? await api.one<unknown>("/api/negative-observations/stats")); } catch { /* best-effort */ }
      // Per-observation detail data so the observation detail page works offline:
      // root-cause analysis + evidence attachments (list + bytes). Prefer the
      // bundle's aux.byNom (folded — no extra call); fall back to the standalone
      // /warmup-bundle, then to per-NOM. Keys MUST match what RootCausesSection /
      // ObservationDetail read (`root-causes:<kind>:<id>`, `attachments:<kind>:<id>`).
      try {
        const nb = warmAux.negativeObservations?.byNom
          ?? await gated(() => api.one<Record<string, { rootCauses?: unknown; attachments?: Attachment[] }>>("/api/negative-observations/warmup-bundle"));
        await mapLimit(Object.keys(nb), 6, async (key) => {
          const entry = nb[key]!;
          try { await repo.kvPut(`root-causes:negative_observation:${key}`, entry.rootCauses); } catch { /* best-effort */ }
          try {
            const ev = entry.attachments ?? [];
            await repo.kvPut(`attachments:negative_observation:${key}`, ev);
            await mapLimit(ev, 4, (a) => cacheAsset(repo, a));
          } catch { /* best-effort */ }
        });
      } catch {
        await mapLimit(obsRows, 4, async (o) => {
          try { await repo.kvPut(`root-causes:negative_observation:${o.id}`, await gated(() => api.one<unknown>(`/api/negative-observations/${o.id}/root-causes`))); } catch { /* best-effort */ }
          try {
            const ev = await gated(() => api.one<Attachment[]>(`/api/attachments?ownerType=negative_observation&ownerId=${o.id}`));
            await repo.kvPut(`attachments:negative_observation:${o.id}`, ev);
            await mapLimit(ev, 4, (a) => cacheAsset(repo, a));
          } catch { /* best-effort */ }
        });
      }
    })(),
    (async () => {
      try {
        const recs = await api.paged<Rectification>("/api/rectifications", { limit: 1000 });
        if (recs.data?.length) await repo.applyDelta({ ...EMPTY_DELTA, rectifications: recs.data });
      } catch { /* best-effort */ }
    })(),
    (async () => { try { await repo.kvPut("vessels:for-filter", await api.paged<Vessel>("/api/vessels", { limit: 200 })); } catch { /* best-effort */ } })(),
    (async () => { try { await repo.kvPut("inspectors:for-filter", await api.get<{ data: { id: number; fullName: string }[] }>("/api/inspectors").then((r) => r.data)); } catch { /* best-effort */ } })(),
    // Deficiency-log findings across all inspections — the Observations list
    // merges these with negative observations (key matches ObservationList's
    // `deficiency-findings:all` readThrough), so USCG/PSC/Flag deficiencies show offline.
    (async () => { try { await repo.kvPut("deficiency-findings:all", await api.one<DeficiencyFindingListItem[]>("/api/deficiency-findings")); } catch { /* best-effort */ } })(),
    // Checklists (for the create/edit-inspection forms) + each published
    // checklist's full question library, so the New-inspection wizard renders
    // offline. Keys match exactly what the forms read via readThrough.
    (async () => {
      // Checklist METADATA (`checklists:all`) is small and the offline questions
      // page reads it to resolve humanEnabled etc., so keep it. We do NOT cache
      // every published checklist's full question LIBRARY here: that was only
      // for the create wizard (online-only), and the libraries the offline
      // questions page actually needs — the ones for the user's own inspections
      // — are seeded from each inspection's question set in the libByChecklist
      // pass below. This drops the heaviest checklist fetches from warm-up.
      try {
        await repo.kvPut("checklists:all", await api.paged<Checklist>("/api/checklists", { limit: 200 }));
      } catch { /* best-effort */ }
    })(),
    // (inspectors:for-filter is fetched once above — the duplicate call here was
    // redundant and has been removed.)
    (async () => { try { await repo.kvPut("ai:dashboard-summary", await api.one<unknown>("/api/analytics/dashboard-summary")); } catch { /* best-effort */ } })(),
    // Shore-staff dashboard review queues (keys match ShoreStaffDashboard's readThrough).
    (async () => { if (ctx.role === "shore_staff") { try { await repo.kvPut("shore:pending-review", await api.paged<Inspection>("/api/inspections", { status: "submitted,in_review", limit: 8, sort: "submittedAt", order: "asc" })); } catch { /* best-effort */ } } })(),
    (async () => { if (ctx.role === "shore_staff") { try { await repo.kvPut("shore:recent-completed", await api.paged<Inspection>("/api/inspections", { status: "completed", limit: 5, sort: "completedAt", order: "desc" })); } catch { /* best-effort */ } } })(),
    // Reference data (global, small) the observation detail page needs.
    (async () => { try { await repo.kvPut("root-cause-categories", await api.one<unknown>("/api/root-cause-categories")); } catch { /* best-effort */ } })(),
    (async () => { try { await repo.kvPut("ranks:ref", await api.one<unknown>("/api/ranks")); } catch { /* best-effort */ } })(),
    // Notifications (page list + bell count/latest) so they read offline.
    (async () => { try { await repo.kvPut("notifications:list:all", await api.paged<Notification>("/api/notifications", { limit: 50 })); } catch { /* best-effort */ } })(),
    (async () => { try { await repo.kvPut("notifications:unread-count", await api.get<{ data: { count: number } }>("/api/notifications/unread-count").then((r) => r.data.count)); } catch { /* best-effort */ } })(),
    (async () => { try { await repo.kvPut("notifications:latest-5", await api.paged<Notification>("/api/notifications", { limit: 5 })); } catch { /* best-effort */ } })(),
    // Settings: the user's company (the /settings page's main query).
    (async () => { if (ctx.companyId != null) { try { await repo.kvPut(`company:${ctx.companyId}`, await api.one<Company>(`/api/companies/${ctx.companyId}`)); } catch { /* best-effort */ } } })(),
    (async () => { try { await repo.kvPut("mfa:status", await api.one<{ enabled: boolean }>("/api/mfa/status")); } catch { /* best-effort */ } })(),
    // SMS library: the list plus the text of each document whose revision
    // changed since last time, so procedures read and search offline. Loaded
    // lazily — the library's code stays out of the main bundle.
    (async () => { try { await (await import("@/pages/sms/sms-api")).warmSmsLibrary(repo); } catch { /* best-effort */ } })(),
  ]);

  // Per-checklist library union, accumulated from the in-scope question sets
  // below and seeded after the loop for checklists ABSENT from /api/checklists —
  // notably the unpublished GLOBAL PIQ checklist (company_id NULL, super-admin
  // managed), whose question library is otherwise never cached. Without it, the
  // questions page's offline fallback has nothing to render for an un-warmed PIQ.
  const libByChecklist = new Map<number, Map<number, ChecklistQuestion>>();

  await mapLimit(toWarm, 3, async (insp) => {
    const id = insp.id;
    const sig = String(insp.updatedAt ?? ""); // remembered in `versions` once cached

    const wb = warmBundle.get(id);
    let inspNetLoss = false;
    let coreOk = true; // detail + questions + answers all cached → openable offline
    const fail = (e: unknown, critical: boolean) => {
      if (isWarmNetworkError(e)) { inspNetLoss = true; if (critical) coreOk = false; }
      else if (critical) coreOk = false;
    };
    // Cache each piece INDEPENDENTLY so one failure can't strand the others.
    try { await repo.kvPut(ck.inspection(id, ctx.userId), wb ? wb.detail : await gated(() => api.one<unknown>(`/api/inspections/${id}`))); } catch (e) { fail(e, true); }
    try {
      const qs = (wb && wb.questions) ? wb.questions : await gated(() => api.paged<ChecklistQuestion>(`/api/inspections/${id}/questions`, { allRanks: 1 }));
      await repo.kvPut(ck.questions(id, ctx.userId, ctx.role, ctx.rank), qs);
      if (insp.checklistId != null) {
        let m = libByChecklist.get(insp.checklistId);
        if (!m) { m = new Map<number, ChecklistQuestion>(); libByChecklist.set(insp.checklistId, m); }
        for (const q of (qs as QuestionsPayload)?.data ?? []) m.set(q.id, q);
      }
    } catch (e) { fail(e, insp.class !== "deficiency_log"); } // deficiency logs have no checklist
    try { await repo.kvPut(ck.summary(id, ctx.userId, ctx.role, ctx.rank), wb ? wb.summary : await gated(() => api.one<QuestionAnswerSummaryRow[]>(`/api/inspections/${id}/answers/summary`))); } catch (e) { fail(e, false); }
    try {
      const bundle = wb ? wb.answers : await gated(() => api.one<AnswersBundle>(`/api/inspections/${id}/answers`));
      await repo.applyDelta({
        serverTime: "", inspections: [], answers: bundle.envelopes, aspects: bundle.aspects,
        humanRankResponses: bundle.humanRankResponses, photoComparisons: bundle.photoComparisons,
        negativeObservations: [], rectifications: [], ranks: [],
      });
    } catch (e) { fail(e, true); }
    // Photo-comparison references — the previous inspection's photograph of
    // each location, which is the thing Chapter 11 asks the inspector to
    // compare against and which the app has never shown them.
    //
    // The BYTES are warmed too, not just the list. A reference the inspector
    // cannot see while standing at the equipment is no reference at all, and
    // the images belong to a PRIOR inspection so nothing else warms them.
    // That is real bandwidth — roughly one image per photo question — so it is
    // gated and size-capped like every other asset, and a failure costs the
    // reference, never the inspection.
    try {
      const refs = await gated(() =>
        api.one<Array<{ attachmentId: number }>>(`/api/inspections/${id}/photo-references`)
          .catch(() => [] as Array<{ attachmentId: number }>),
      );
      await repo.kvPut(`photo-references:${id}`, refs);
      await mapLimit(refs, 4, async (r) => {
        // Same shape cacheAsset expects; the reference rows are not Attachment
        // records, so the id and a nominal category are supplied directly.
        try {
          if (await repo.getAsset(r.attachmentId)) return;
        } catch { /* fall through and fetch */ }
        try {
          const res = await gated(() => fetch(apiUrl(`/api/attachments/${r.attachmentId}/raw`), {
            headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {},
            credentials: "include",
          }));
          if (res.ok) await repo.putAsset(r.attachmentId, await res.blob());
        } catch { /* best-effort */ }
      });
    } catch { /* best-effort */ }

    // Recurrence digest — what this vessel has been pulled up on before.
    //
    // Warmed rather than fetched on demand because the moment it is worth
    // anything is while the inspector is WRITING a finding, on a vessel, with
    // no connectivity. Unlike the other intelligence features this one is
    // deterministic, so the whole answer set can be computed ashore and carried
    // along. Tens of rows — one per (question code, aspect) the vessel has ever
    // had a finding on.
    //
    // Best-effort: a hint that fails to warm costs a hint, never the inspection.
    try {
      await repo.kvPut(
        `recurrence-digest:${id}`,
        await gated(() =>
          api.one<unknown[]>(`/api/inspections/${id}/recurrence-digest`).catch(() => [] as unknown[]),
        ),
      );
    } catch { /* best-effort */ }

    // Response attachments: per-owner LISTS (keyed as useAspectAttachments reads
    // them) + the bytes. List comes from the bundle; bytes are still fetched.
    try {
      const atts = (wb && Array.isArray(wb.responseAttachments)) ? wb.responseAttachments
        : await gated(() => api.one<Attachment[]>(`/api/inspections/${id}/response-attachments`).catch(() => [] as Attachment[]));
      await repo.kvPut(`inspection-response-attachments:${id}`, atts);
      const byOwner = new Map<string, Attachment[]>();
      for (const a of atts) {
        const k = `attachments:${a.ownerType}:${a.ownerId}`;
        const arr = byOwner.get(k);
        if (arr) arr.push(a); else byOwner.set(k, [a]);
      }
      for (const [k, listForOwner] of byOwner) await repo.kvPut(k, listForOwner);
      await mapLimit(atts, 6, (a) => cacheAsset(repo, a));
    } catch (e) { fail(e, false); }
    // Inspection-LEVEL attachments (ownerType=inspection) — list (bundle) + bytes.
    try {
      const inspAtts = (wb && Array.isArray(wb.inspectionAttachments)) ? wb.inspectionAttachments
        : await gated(() => api.one<Attachment[]>(`/api/attachments?ownerType=inspection&ownerId=${id}`));
      await repo.kvPut(`attachments:inspection:${id}`, inspAtts);
      await mapLimit(inspAtts, 6, (a) => cacheAsset(repo, a));
    } catch (e) { fail(e, false); }

    // Deficiency-log inspections (USCG / PSC / Flag State) walk findings, not a
    // checklist. Cache the findings list + the regime's code pickers + each
    // finding's single-DTO and root-causes so DeficiencyLog / FindingDetailPage
    // render fully offline. Keyed to match the pages' readThrough keys.
    if (insp.class === "deficiency_log") {
      const regime =
        insp.type === "uscg" ? "uscg" : insp.type === "flag_state" ? "flag_state" : "imo_psc";
      // Prefer the warm-up bundle's deficiencyLog sub-payload (one request);
      // fall back to the per-finding fetches when it's absent (bundle 404 /
      // inspection synced after the bundle was built).
      const dl = wb?.deficiencyLog;
      try { await repo.kvPut(`psc-deficiency-codes:${regime}`, dl ? dl.pscDeficiencyCodes : await gated(() => api.one<DeficiencyCode[]>(`/api/psc-codes/deficiencies?regime=${regime}`))); } catch { /* best-effort */ }
      try { await repo.kvPut(`psc-action-codes:${regime}`, dl ? dl.pscActionCodes : await gated(() => api.one<ActionCode[]>(`/api/psc-codes/actions?regime=${regime}`))); } catch { /* best-effort */ }
      try {
        const findings = dl ? dl.findings : await gated(() => api.one<DeficiencyFindingWithCodes[]>(`/api/inspections/${id}/findings`));
        await repo.kvPut(`findings:${id}`, findings);
        await mapLimit(findings, 6, async (fwc) => {
          const fid = fwc.finding.id;
          try {
            const detail = dl?.findingDetails[String(fid)] ?? await gated(() => api.one<DeficiencyFindingListItem>(`/api/deficiency-findings/${fid}`));
            await repo.kvPut(`deficiency-finding:${fid}`, detail);
          } catch { /* best-effort */ }
          try { await repo.kvPut(`root-causes:deficiency_finding:${fid}`, dl ? (dl.findingRootCauses[String(fid)] ?? []) : await gated(() => api.one<ObservationRootCause[]>(`/api/deficiency-findings/${fid}/root-causes`))); } catch { /* best-effort */ }
          // Finding attachments (list + bytes) — key matches FindingAttachments' readThrough.
          try {
            const fatts = dl ? (dl.findingAttachments[String(fid)] ?? []) : await gated(() => api.one<Attachment[]>(`/api/attachments?ownerType=deficiency_finding&ownerId=${fid}`));
            await repo.kvPut(`attachments:deficiency_finding:${fid}`, fatts);
            await mapLimit(fatts, 6, (a) => cacheAsset(repo, a));
          } catch (e) { fail(e, false); }
        });
      } catch (e) { fail(e, false); }
      try {
        const allFindingAtts = await gated(() =>
          api.one<FindingAttachment[]>(`/api/inspections/${id}/finding-attachments`)
            .catch(() => [] as FindingAttachment[]),
        );
        await repo.kvPut(`inspection-finding-attachments:${id}`, allFindingAtts);
        await mapLimit(allFindingAtts, 6, (a) => cacheAsset(repo, a));
      } catch (e) { fail(e, false); }
    }

    // Mark warmed only if the CRITICAL caches landed and no connectivity loss —
    // so a transient failure re-warms next time instead of being skipped forever.
    // `warmedIds` drives in-session failure recovery (skipIds on retry); the
    // `versions` sig persists so the next cold start skips this unless it changes.
    if (inspNetLoss) networkLoss = true;
    if (coreOk && !inspNetLoss) {
      warmedIds.push(id);
      if (sig) versions[String(id)] = sig;
    }
  });

  // Response/inspection-level attachments are cached in the per-inspection loop
  // ONLY for `toWarm` (new/changed inspections). But a photo attached to a
  // question response doesn't bump the inspection's updatedAt, so an inspection
  // that's otherwise unchanged would be skipped and its NEW evidence would never
  // cache → the Questions page shows the row but a blank image offline. Refresh
  // the attachment LISTS for the SKIPPED inspections on EVERY warm-up (like the
  // NOM evidence bundle already does). cacheAsset skips bytes already cached, so
  // this only downloads genuinely-new files; the list fetch is a small JSON.
  const skipped = inspections.filter((i) => !toWarm.some((t) => t.id === i.id));
  if (skipped.length) {
    await mapLimit(skipped, 4, async (insp) => {
      const id = insp.id;
      try {
        const atts = await gated(() => api.one<Attachment[]>(`/api/inspections/${id}/response-attachments`).catch(() => [] as Attachment[]));
        await repo.kvPut(`inspection-response-attachments:${id}`, atts);
        const byOwner = new Map<string, Attachment[]>();
        for (const a of atts) {
          const k = `attachments:${a.ownerType}:${a.ownerId}`;
          const arr = byOwner.get(k); if (arr) arr.push(a); else byOwner.set(k, [a]);
        }
        for (const [k, listForOwner] of byOwner) await repo.kvPut(k, listForOwner);
        await mapLimit(atts, 6, (a) => cacheAsset(repo, a));
      } catch (e) { if (isWarmNetworkError(e)) networkLoss = true; }
      try {
        const inspAtts = await gated(() => api.one<Attachment[]>(`/api/attachments?ownerType=inspection&ownerId=${id}`));
        await repo.kvPut(`attachments:inspection:${id}`, inspAtts);
        await mapLimit(inspAtts, 6, (a) => cacheAsset(repo, a));
      } catch (e) { if (isWarmNetworkError(e)) networkLoss = true; }
      // Deficiency-log finding attachments (the "Finding attachments" section of
      // the Attachments page) — same every-warm refresh so a file added to an
      // already-warmed finding still renders offline.
      if (insp.class === "deficiency_log") {
        try {
          const allFindingAtts = await gated(() =>
            api.one<FindingAttachment[]>(`/api/inspections/${id}/finding-attachments`).catch(() => [] as FindingAttachment[]),
          );
          await repo.kvPut(`inspection-finding-attachments:${id}`, allFindingAtts);
          await mapLimit(allFindingAtts, 6, (a) => cacheAsset(repo, a));
        } catch (e) { if (isWarmNetworkError(e)) networkLoss = true; }
      }
    });
  }

  // Persist the warmed-versions map (pruned to inspections that still exist) so
  // the next app open skips everything unchanged.
  try {
    const currentIds = new Set(inspections.map((i) => String(i.id)));
    const pruned: Record<string, string> = {};
    for (const k of Object.keys(versions)) if (currentIds.has(k)) pruned[k] = versions[k]!;
    await repo.kvPut(WARMED_VERSIONS_KEY, pruned);
  } catch { /* best-effort */ }

  // Seed checklist-questions:<cid> for any checklist whose full library wasn't
  // already warmed from /api/checklists (that block only covers PUBLISHED,
  // listable checklists). This fills in the unpublished global PIQ checklist so
  // the questions page's offline fallback (localTempQuestions) can rebuild a PIQ
  // that wasn't itself warmed (synced after warm-up / never opened online).
  for (const [cid, qmap] of libByChecklist) {
    try {
      if ((await repo.kvGet(`checklist-questions:${cid}`)) !== undefined) continue;
      const data = [...qmap.values()];
      await repo.kvPut(`checklist-questions:${cid}`, {
        success: true, data,
        meta: { page: 1, limit: data.length || 1, total: data.length, totalPages: 1 },
      });
    } catch { /* best-effort */ }
  }

  // complete = no connectivity failure anywhere. The caller marks warm-up done
  // (and stops re-warming) only when complete; otherwise it retries on the next
  // online tick, skipping warmedIds so the resume only fills the gaps.
  return { complete: !networkLoss, warmedIds };
}

/** Refresh the AGGREGATED attachment caches (response / inspection-level /
 *  finding) for specific inspections — the keys the offline Attachments page
 *  reads. Called after a sync uploads offline attachments so they show offline
 *  WITHOUT an explicit online visit to that page (warm-up only runs once per
 *  session, and the pull delta doesn't carry these aggregated snapshots).
 *  Best-effort; requires connectivity (the caller runs it right after a sync). */
export async function refreshInspectionAttachments(repo: Repo, inspectionIds: number[]): Promise<void> {
  const ids = [...new Set(inspectionIds.filter((n) => typeof n === "number" && n > 0))];
  if (!ids.length) return;
  await mapLimit(ids, 4, async (id) => {
    try {
      const atts = await gated(() => api.one<Attachment[]>(`/api/inspections/${id}/response-attachments`).catch(() => [] as Attachment[]));
      await repo.kvPut(`inspection-response-attachments:${id}`, atts);
      const byOwner = new Map<string, Attachment[]>();
      for (const a of atts) {
        const k = `attachments:${a.ownerType}:${a.ownerId}`;
        const arr = byOwner.get(k); if (arr) arr.push(a); else byOwner.set(k, [a]);
      }
      for (const [k, listForOwner] of byOwner) await repo.kvPut(k, listForOwner);
      await mapLimit(atts, 6, (a) => cacheAsset(repo, a));
    } catch { /* best-effort */ }
    try {
      const inspAtts = await gated(() => api.one<Attachment[]>(`/api/attachments?ownerType=inspection&ownerId=${id}`));
      await repo.kvPut(`attachments:inspection:${id}`, inspAtts);
      await mapLimit(inspAtts, 6, (a) => cacheAsset(repo, a));
    } catch { /* best-effort */ }
    try {
      const findingAtts = await gated(() => api.one<FindingAttachment[]>(`/api/inspections/${id}/finding-attachments`).catch(() => [] as FindingAttachment[]));
      await repo.kvPut(`inspection-finding-attachments:${id}`, findingAtts);
      await mapLimit(findingAtts, 6, (a) => cacheAsset(repo, a));
    } catch { /* best-effort */ }
  });
}

/** Warm a SINGLE inspection's enriched DTOs (detail, questions, summary, answer
 *  bundle) + its response/inspection-level attachments into the cache, so it can
 *  be OPENED offline. Best-effort: a failure never throws. Used to warm just the
 *  newly-created inspection right after an online create (see seedInspectionOffline). */
export async function warmInspectionDetail(repo: Repo, id: number, ctx: WarmCtx): Promise<void> {
  try {
    const [detail, questions, summary, bundle] = await Promise.all([
      api.one<unknown>(`/api/inspections/${id}`),
      api.paged<ChecklistQuestion>(`/api/inspections/${id}/questions`, { allRanks: 1 }),
      api.one<QuestionAnswerSummaryRow[]>(`/api/inspections/${id}/answers/summary`),
      api.one<{ envelopes: QuestionAnswer[]; aspects: QuestionAspectResponse[]; photoComparisons: PhotoComparison[]; humanRankResponses: InspectionHumanRankResponse[] }>(`/api/inspections/${id}/answers`),
    ]);
    await repo.kvPut(ck.inspection(id, ctx.userId), detail);
    await repo.kvPut(ck.questions(id, ctx.userId, ctx.role, ctx.rank), questions);
    await repo.kvPut(ck.summary(id, ctx.userId, ctx.role, ctx.rank), summary);
    await repo.applyDelta({
      serverTime: "", inspections: [], answers: bundle.envelopes, aspects: bundle.aspects,
      humanRankResponses: bundle.humanRankResponses, photoComparisons: bundle.photoComparisons,
      negativeObservations: [], rectifications: [], ranks: [],
    });
    // Attachments: per-owner lists (keyed as useAspectAttachments reads them) + bytes.
    const atts = await api.one<Attachment[]>(`/api/inspections/${id}/response-attachments`).catch(() => [] as Attachment[]);
    await repo.kvPut(`inspection-response-attachments:${id}`, atts);
    const byOwner = new Map<string, Attachment[]>();
    for (const a of atts) {
      const k = `attachments:${a.ownerType}:${a.ownerId}`;
      const arr = byOwner.get(k);
      if (arr) arr.push(a); else byOwner.set(k, [a]);
    }
    for (const [k, listForOwner] of byOwner) await repo.kvPut(k, listForOwner);
    await mapLimit(atts, 4, (a) => cacheAsset(repo, a));
    try {
      const inspAtts = await gated(() => api.one<Attachment[]>(`/api/attachments?ownerType=inspection&ownerId=${id}`));
      await repo.kvPut(`attachments:inspection:${id}`, inspAtts);
      await mapLimit(inspAtts, 4, (a) => cacheAsset(repo, a));
    } catch { /* best-effort */ }
  } catch { /* best-effort — one inspection failing must not throw */ }
}
